import * as _ from 'lodash';
import { WorkerRequestChannelInfo, WorkerRequestParams } from "../ChartWorkerManager";
import {
    CHART_AGGREGATION_PERIOD,
    CHART_AGGREGATION_TYPE,
    CHART_TYPE,
    ChartConfig,
    ChartConfigSeriesGroup
} from "./ChartHelper";

// IE 11 Polyfill, since operative expects window.Promise to exist
if (!window['Promise']) window['Promise'] = require('lib/Promise.min');
var operative = require('operative');


interface ProcessSeriesInterface {
    seriesGroup: ChartConfigSeriesGroup,
    timezone: string,
    requestInfo: any
}

module.exports = function () {
    return window['operative']({
        CHART_TYPE: _.clone(CHART_TYPE),
        CHART_AGGREGATION_PERIOD: _.clone(CHART_AGGREGATION_PERIOD),
        CHART_AGGREGATION_TYPE: _.clone(CHART_AGGREGATION_TYPE),


        _getPeriodUnit(period: string) {
            switch(period) {
                case this.CHART_AGGREGATION_PERIOD.MINUTE_15:
                    return 15 * 60;
                case this.CHART_AGGREGATION_PERIOD.MINUTE_30:
                    return 30 * 60;
                case this.CHART_AGGREGATION_PERIOD.HOUR:
                    return 'hour';
                case this.CHART_AGGREGATION_PERIOD.DAY:
                    return 'day';
                case this.CHART_AGGREGATION_PERIOD.WEEK:
                    return 'isoweek';
                case this.CHART_AGGREGATION_PERIOD.MONTH_1:
                    return 'month';
                case this.CHART_AGGREGATION_PERIOD.MONTH_3:
                    return '3month';
                case this.CHART_AGGREGATION_PERIOD.MONTH_6:
                    return '6month';
                case this.CHART_AGGREGATION_PERIOD.YEAR:
                    return 'year';
                case this.CHART_AGGREGATION_PERIOD.RANGE:
                    return 'range';
                case this.CHART_AGGREGATION_PERIOD.DEFAULT:
                    return;
                default:
                    return period;
            }
        },

        _getAggregationType(type: string) {
            switch(type) {
                case this.CHART_AGGREGATION_TYPE.AVG:
                    return 'mean';
                case this.CHART_AGGREGATION_TYPE.SUM:
                    return 'sum';
                case this.CHART_AGGREGATION_TYPE.MIN:
                    return 'min';
                case this.CHART_AGGREGATION_TYPE.MAX:
                    return 'max';
                case this.CHART_AGGREGATION_TYPE.MAXDIFF:
                    return 'pd';
                case this.CHART_AGGREGATION_TYPE.MAXDIFFPOSITIVE:
                    return 'pdpos';
                case this.CHART_AGGREGATION_TYPE.MODE:
                    return 'mode';
                case this.CHART_AGGREGATION_TYPE.MEDIAN:
                    return 'median';
                case this.CHART_AGGREGATION_TYPE.STDDEV:
                    return 'std_dev';
                case this.CHART_AGGREGATION_TYPE.BOXPLOT:
                    return 'min,lower_quartile,median,upper_quartile,max';
                case this.CHART_AGGREGATION_TYPE.PERCENTILE95:
                    return 'percentile_95';
                case this.CHART_AGGREGATION_TYPE.PERCENTILE5:
                    return 'percentile_5';
            }
        },

        _getFormattedCategory: function (moment, period) {
            if (period === 'minute') {
                return moment.format('HH:mm MM/DD')
            } else if (period === 'hour') {
                return moment.format('hA MM/DD')
            } else if (period === 'day') {
                return moment.format('MM/DD')
            } else if (period === 'week' || period === 'isoweek') {
                return moment.format('MM/DD')
            } else if (period === 'month') {
                return moment.format('MMM')
            } else if (period === 'year') {
                return moment.format('YYYY')
            }
        },

        data: {},
        startTimestamps: {},
        endTimestamps: {},

        _getApiRequest(params: WorkerRequestParams, chartConfig: ChartConfig, callback) {
            var _ = this._;
            var moment = this.moment;

            var now = Math.floor(Date.now() / 1000);
            // If we're loading more than one week of data use the historic endpoint, which is monthly data.
            if (now - params.startTimestamp > (8 * 86400)) {
                var endpoint = 'historic'
            } else {
                var endpoint = 'recent'
            }
            var url = params.urlOrigin + '/v1/' + endpoint + '?';

            // Set the timezone
            var qs = 'tz=' + chartConfig.timezone;

            // Set the data start date
            qs += '&startDate=' + moment.tz(params.startTimestamp * 1000, chartConfig.timezone).format('YYYYMMDD');

            if (params.endTimestamp) {
                qs += '&endDate=' + moment.tz(params.endTimestamp * 1000, chartConfig.timezone).format('YYYYMMDD');
            }

            var requestInfo = {
                series: null,
                seriesGroup: null,
            };
            _.each(params.channels, (c: WorkerRequestChannelInfo) => {
                var id = c.id;
                var secret = c.channel_secret;

                var series, seriesGroup;

                // only one series group per chart type
                if (chartConfig.seriesGroups.length === 1) {
                    seriesGroup = chartConfig.seriesGroups[0];
                    series = chartConfig.seriesGroups[0].series;
                } else {
                    // Loop through series groups until we find the one with the correct tag
                    var seriesGroups = _.filter(chartConfig.seriesGroups, (g: ChartConfigSeriesGroup) => {
                        return _.findWhere(g.series, {channelId: id})
                    });
                    _.each(seriesGroups, (g) => {
                        var s = _.filter(g.series, (s) => {
                            return s.channelId === id && _.includes(c.tags, s.tag)
                        });

                        if (s && s.length) {
                            series = s;
                            seriesGroup = g;
                        }
                    });
                }

                if (!series || !seriesGroup) {
                    console.log('Warning: Series or SeriesGroup not found', c, chartConfig);
                    throw "Warning: Series or SeriesGroup not found";
                }
                if (!requestInfo.seriesGroup) {
                    requestInfo.series = series;
                    requestInfo.seriesGroup = seriesGroup;
                }
                if (!_.isEqual(series, requestInfo.series) || !_.isEqual(seriesGroup, requestInfo.seriesGroup)) {
                    throw "Expected all series and the series group to be equal for a single worker";
                }

                var period = this._getPeriodUnit(seriesGroup.period);
                var agg = [];
                _.each(series, (s) => {
                    if (s.channelId !== id) return;
                    agg.push(this._getAggregationType(s.agg))
                });
                qs += '&' + id + '.secret=' + secret;
                requestInfo[id] = {
                    secret: secret
                };

                if (!chartConfig.convertAllValues) {
                    qs += '&' + id + '.raw=true';
                    requestInfo[id]['raw'] = true;
                }

                if (!chartConfig.realTimestamps) {
                    qs += '&' + id + '.roundTimestamps=' + c.polling_frequency;
                    requestInfo[id]['roundTimestamps'] = c.polling_frequency;
                }

                if (params.limitDataPoints) {
                    qs += '&' + id + '.limit=' + params.limitDataPoints;
                    requestInfo[id]['limitDataPoints'] = params.limitDataPoints;
                }
                if (period && agg.length) {
                    qs += '&' + id + '.period=' + period;
                    qs += '&' + id + '.func=' + agg.join();
                    requestInfo[id]['period'] = period;
                    requestInfo[id]['func'] = agg;
                }

                // Increase the gapsize for dashboards
                if (!period && chartConfig.hideSingleGaps) {
                    qs += '&' + id + '.gapSize=2';
                    requestInfo[id]['gapSize'] = 2;
                }
            });

            var xmlhttp = new XMLHttpRequest();
            xmlhttp.onreadystatechange = () => {
                if (xmlhttp.readyState == 4) {
                    if (xmlhttp.status == 200) {
                        callback(false, JSON.parse(xmlhttp.responseText), requestInfo);
                    } else {
                        callback(true, []);
                    }
                }
            };

            xmlhttp.open("GET", url + qs, true);
            // We have to lie about the content type to make Internet Explorer 11 actually
            // download the content
            xmlhttp.setRequestHeader("Content-Type", "text/plain");
            xmlhttp.send();
        },

        _isMultiChannelSeries(chartType) {
            return this._.includes([
                this.CHART_TYPE.HEATMAP,
                this.CHART_TYPE.PIE,
                this.CHART_TYPE.WIDGET_MULTI_SERIES_SINGLE_POINT,
                this.CHART_TYPE.WIDGET_MULTI_SERIES
            ], chartType);
        },

        _isSingleChannelMultipleAggregationSeries(chartType) {
            return this._.includes([
                this.CHART_TYPE.BOX_PLOT
            ], chartType);
        },

        _mergeData(workerId: string, data: any) {
            this._.each(data, (values, channelId) => {
                if (!this.data[workerId]) this.data[workerId] = {};

                if (!this.data[workerId][channelId] || !this.data[workerId][channelId].length) {
                    this.data[workerId][channelId] = values;
                    return
                }

                if (!values.length) return;

                var firstSeries, secondSeries;
                if (this.data[workerId][channelId][0][0] <= values[0][0]) {
                    firstSeries = this.data[workerId][channelId];
                    secondSeries = values;
                } else {
                    secondSeries = this.data[workerId][channelId];
                    firstSeries = values;
                }

                var k = -1;
                var ts = secondSeries[0][0];
                for(var i=0; i<firstSeries.length; i++) {
                    if (ts <= firstSeries[i][0]) {
                        k = i;
                        break;
                    }
                }

                this.data[workerId][channelId] = firstSeries.slice(0, k).concat(secondSeries);
            })
        },

        processData(params: WorkerRequestParams, options, callback) {
            var chartConfig: ChartConfig = options.chartConfig;
            var _ = this._;
            var wId = params.workerId;

            this._getApiRequest(params, chartConfig, (err, data, requestInfo) => {
                if(err) return callback(err);

                if (!this.startTimestamps[wId]) this.startTimestamps[wId] = {};
                if (!this.endTimestamps[wId]) this.endTimestamps[wId] = {};

                var rtn = {};
                if (params.keepData) {
                    this._mergeData(wId, data);
                    _.each(params.channels, (channel, channelId) => {
                        this.startTimestamps[wId][channelId] = this.startTimestamps[wId][channelId] ? Math.min(
                            this.startTimestamps[wId][channelId],
                            params.startTimestamp * 1000
                        ) : params.startTimestamp * 1000;
                        this.endTimestamps[wId][channelId] = this.endTimestamps[wId][channelId] ? Math.max(
                            this.endTimestamps[wId][channelId],
                            params.endTimestamp * 1000
                        ) : params.endTimestamp * 1000;
                    });
                } else {
                    if (!this.data[wId]) this.data[wId] = {};

                    this.data[wId] = data;
                    this.startTimestamps[wId] = {};
                    this.endTimestamps[wId] = {};
                    _.each(params.channels, (channel, channelId) => {
                        this.startTimestamps[wId][channelId] = params.startTimestamp * 1000;
                        this.endTimestamps[wId][channelId] = params.endTimestamp * 1000;
                    });
                }

                var options = {
                    timezone: chartConfig.timezone,
                    requestInfo: requestInfo
                };

                // Here we find if the series has a combination of channel sources, i.e. pie and heatmap
                if (this._isMultiChannelSeries(requestInfo.seriesGroup.type)) {
                    var result = this._processMultiChannelSeries(wId, options);
                } else if(this._isSingleChannelMultipleAggregationSeries(requestInfo.seriesGroup.type)) {
                    var result = this._processSingleChannelMultipleAggregationSeries(wId, options);
                } else {
                    var result = this._processSingleChannelSeries(wId, options);
                }

                _.extend(rtn, result);

                return callback(null, rtn);
            });
        },

        // singleChannelSeries = one channel, one aggregation => one series
        _processSingleChannelSeries(workerId: string, options: ProcessSeriesInterface) {
            var _ = this._;
            var requestInfo = options.requestInfo;
            var taggedData = {};
            var seriesByChannelId = _.groupBy(requestInfo.series, (s) => {return s.channelId});

            _.each(seriesByChannelId, (seriesGroup) => {
                _.each(seriesGroup, (series, i) => {
                    var id = Number(series.channelId);
                    if (!this.data[workerId] || !this.data[workerId][id]) return;

                    taggedData[series.tag] = [];
                    _.each(this.data[workerId][id], (dataPoint) => {
                        // Filter out data that is before 'after'
                        if (dataPoint[0] < this.startTimestamps[workerId][id] || dataPoint[0] > this.endTimestamps[workerId][id]) {
                            return;
                        }

                        // For dashboard channels that have the same channel, but different aggregation functions
                        // the value will be at i + 1
                        // For dashboard channels that have the same channel, but no aggregation function the
                        // value will be at 1
                        var j = (dataPoint.length - 1) >= i + 1 ? i + 1 : 1;
                        taggedData[series.tag].push([dataPoint[0], dataPoint[j]]);
                    });
                });
            });
            return taggedData;
        },

        // one channel, multiple aggregations => one series
        _processSingleChannelMultipleAggregationSeries(workerId: string, options: ProcessSeriesInterface) {
            var _ = this._;
            var requestInfo = options.requestInfo;
            var taggedData = {};
            var seriesByChannelId = _.groupBy(requestInfo.series, (s) => {return s.channelId});

            _.each(seriesByChannelId, (seriesGroup) => {
                _.each(seriesGroup, (series, i) => {
                    var id = Number(series.channelId);
                    if (!this.data[workerId] || !this.data[workerId][id]) return;

                    taggedData[series.tag] = [];
                    _.each(this.data[workerId][id], (dataPoint) => {
                        // Filter out data that is before 'after'
                        if (dataPoint[0] < this.startTimestamps[workerId][id] || dataPoint[0] > this.endTimestamps[workerId][id]) {
                            return;
                        }

                        // _.groupBy orders group in the order that they occur in
                        taggedData[series.tag].push(dataPoint);
                    });
                });
            });
            return taggedData;
        },

        // multiChannelSeries = multiple channels; one aggregation each => one series
        _processMultiChannelSeries(workerId: string, options: ProcessSeriesInterface) {
            var _ = this._;
            var moment = this.moment;
            var requestInfo = options.requestInfo;

            var returnData = [];
            var returnDataTagged = {};
                _.each(requestInfo.series, (series, i) => {
                var id = Number(series.channelId);
                if (!this.data[workerId] || !this.data[workerId][id]) return;

                // Figure at which index our data is located (in case the same channel was used multiple times
                // with different aggregation functions)
                var dataIndex = _.indexOf(requestInfo[id]['func'], this._getAggregationType(series.agg));

                // The first index is always the timestamp
                dataIndex++;
                if (!this.data[workerId][id].length && requestInfo.seriesGroup.type === this.CHART_TYPE.WIDGET_MULTI_SERIES) {
                    returnDataTagged[series.tag] = [];
                }
                var isSinglePointSeries = requestInfo.seriesGroup.type === this.CHART_TYPE.WIDGET_MULTI_SERIES_SINGLE_POINT;
                _.each(this.data[workerId][id], (tile) => {
                    if (!isSinglePointSeries) {
                        if (tile[0] < this.startTimestamps[workerId][id] || tile[0] > this.endTimestamps[workerId][id]) return;
                    }

                    if (requestInfo.seriesGroup.type === this.CHART_TYPE.HEATMAP) {
                        if (tile[dataIndex] == null) return;

                        returnData.push([
                            tile[0],
                            i,
                            tile[dataIndex]
                        ]);
                    } else if (requestInfo.seriesGroup.type === this.CHART_TYPE.PIE) {
                        // Pie charts can only have positive slices
                        if (!tile[dataIndex] || tile[dataIndex] < 0) return;

                        returnData.push({
                            'name': this._getFormattedCategory(
                                moment.tz(tile[0], options.timezone),
                                this._getPeriodUnit(requestInfo.seriesGroup.period)
                            ),
                            'category': series.name,
                            'y': tile[dataIndex]
                        });
                    } else if (requestInfo.seriesGroup.type === this.CHART_TYPE.WIDGET_MULTI_SERIES_SINGLE_POINT) {
                        if(tile[dataIndex] == null) return;
                        returnDataTagged[series.tag] =  [tile[0], tile[dataIndex]]
                    } else if (requestInfo.seriesGroup.type === this.CHART_TYPE.WIDGET_MULTI_SERIES){
                        // TODO figure a way to ensure that a multi channel series can have no agg function
                        if(series.agg === this.CHART_AGGREGATION_PERIOD.NONE) dataIndex = 1;
                        if(tile[dataIndex] == null) return;
                        if(!returnDataTagged[series.tag]) returnDataTagged[series.tag] = [];
                        returnDataTagged[series.tag].push([tile[0], tile[dataIndex]])
                    }
                });
            });

            if (requestInfo.seriesGroup.type === this.CHART_TYPE.HEATMAP) {
                // Sort by timestamp
                returnData = _.sortBy(returnData, '0');
            }

            if (requestInfo.seriesGroup.type === this.CHART_TYPE.WIDGET_MULTI_SERIES_SINGLE_POINT) {
                // single point multi channel series
                returnData = [returnDataTagged];
            }

            if (requestInfo.seriesGroup.type === this.CHART_TYPE.WIDGET_MULTI_SERIES) {
                // single point multi channel series
                returnData = [returnDataTagged];
            }

            return {[requestInfo.seriesGroup.type]: returnData};
        }
    }, [
        // TODO: figure out how to use webpack to load this
        window.location.origin + '/static/js/lib/underscore-min.js',
        window.location.origin + '/static/js/lib/moment.min.js',
        window.location.origin + '/static/js/lib/moment-timezone.min.js',
    ]);
};



// WEBPACK FOOTER //
// ./srv/public/js/views/dashboard/widgets/chart/chartWorker.ts